import type { ColumnSort } from "shared";
import type { MarkdownTableRow } from "./markdownSegments";
import { markdownCellText } from "./markdownSegments";

type SortKey = number | string | null;

const EMPTY_CELLS = new Set(["", "-", "—", "–", "n/a", "na", "none", "null"]);

const NUMBER_NOISE = /[\s,%$£€₹]/g;
const NUMBER_ONLY = /^[-+]?\d+(\.\d+)?$/;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([t ]\d{2}:\d{2}|$)/i;
const SLASH_DATE = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
const MONTH = "jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec";
const DAY_MONTH_YEAR = new RegExp(
  `^\\d{1,2}\\s+(${MONTH})[a-z]*\\.?\\s+\\d{4}$`,
  "i",
);
const MONTH_DAY_YEAR = new RegExp(
  `^(${MONTH})[a-z]*\\.?\\s+\\d{1,2},?\\s+\\d{4}$`,
  "i",
);

const TEXT_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const isEmptyCell = (cell: string): boolean =>
  EMPTY_CELLS.has(cell.toLowerCase());

const toNumber = (cell: string): number | null => {
  const stripped = cell.replace(NUMBER_NOISE, "");
  return NUMBER_ONLY.test(stripped) ? Number(stripped) : null;
};

const toTimestamp = (cell: string): number | null => {
  const looksLikeDate =
    ISO_DATE.test(cell) ||
    SLASH_DATE.test(cell) ||
    DAY_MONTH_YEAR.test(cell) ||
    MONTH_DAY_YEAR.test(cell);

  if (!looksLikeDate) return null;

  const parsed = Date.parse(cell);
  return Number.isNaN(parsed) ? null : parsed;
};

const buildSortKeys = (cells: string[]): SortKey[] => {
  const filled = cells.filter((cell) => !isEmptyCell(cell));

  const asNumbers = filled.map(toNumber);
  if (filled.length > 0 && asNumbers.every((value) => value !== null)) {
    return cells.map((cell) => (isEmptyCell(cell) ? null : toNumber(cell)));
  }

  const asTimestamps = filled.map(toTimestamp);
  if (filled.length > 0 && asTimestamps.every((value) => value !== null)) {
    return cells.map((cell) => (isEmptyCell(cell) ? null : toTimestamp(cell)));
  }

  return cells.map((cell) => (isEmptyCell(cell) ? null : cell.toLowerCase()));
};

const compareKeys = (a: SortKey, b: SortKey, factor: number): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * factor;

  return TEXT_COLLATOR.compare(String(a), String(b)) * factor;
};

export const sortMarkdownTableRows = (
  rows: MarkdownTableRow[],
  column: number,
  direction: ColumnSort,
): MarkdownTableRow[] => {
  if (direction === "natural") return rows;

  const keys = buildSortKeys(
    rows.map((row) => markdownCellText(row.cells[column] ?? "")),
  );
  const factor = direction === "asc" ? 1 : -1;

  return rows
    .map((row, index) => ({ row, key: keys[index] }))
    .sort((a, b) => compareKeys(a.key, b.key, factor))
    .map((entry) => entry.row);
};
